import React from 'react';
import { Users, FileText, DollarSign, BarChart3, Eye, TrendingUp, AlertCircle, Settings } from 'lucide-react';

const AdminDashboardHome = ({ clients, invoices, quotes, settings, onNavigate }) => {
  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;

  // Calculate revenue from paid invoices
  const totalRevenue = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => {
      const subtotal = inv.items?.reduce((itemSum, item) => 
        itemSum + (item.quantity * item.price), 0) || 0;
      const discount = subtotal * (inv.discountPercentage || 0) / 100;
      const afterDiscount = subtotal - discount;
      const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
      return sum + afterDiscount + tax;
    }, 0);

  // Calculate pending quotes value
  const pendingQuotesValue = quotes
    .filter(quote => quote.status === 'pending')
    .reduce((sum, quote) => {
      const subtotal = quote.items?.reduce((itemSum, item) => 
        itemSum + (item.quantity * item.price), 0) || 0;
      const discount = subtotal * (quote.discountPercentage || 0) / 100;
      const afterDiscount = subtotal - discount;
      const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
      return sum + afterDiscount + tax;
    }, 0);

  // Calculate outstanding invoices value
  const outstandingInvoices = invoices
    .filter(inv => inv.status === 'sent' || inv.status === 'overdue')
    .reduce((sum, inv) => {
      const subtotal = inv.items?.reduce((itemSum, item) => 
        itemSum + (item.quantity * item.price), 0) || 0;
      const discount = subtotal * (inv.discountPercentage || 0) / 100;
      const afterDiscount = subtotal - discount;
      const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
      return sum + afterDiscount + tax;
    }, 0);

  // Get recent activity (last 10 items)
  const recentActivity = [...invoices, ...quotes]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10);

  // Status counts
  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue').length;
  const pendingQuotes = quotes.filter(quote => quote.status === 'pending').length;

  return (
    <div>
      {/* Welcome Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-semibold mb-2 text-blue-600">Dashboard Overview</h2>
        <p className="text-gray-600">Welcome back! Here's what's happening in your business</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-blue-100 p-3 rounded-full flex items-center justify-center">
                <Users size={24} className="text-blue-600" />
              </div>
            </div>
            <div className="flex-1 ml-4">
              <div className="text-sm font-medium text-gray-600">Total Clients</div>
              <div className="text-2xl font-bold text-blue-600">{clients.length}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-green-100 p-3 rounded-full flex items-center justify-center">
                <TrendingUp size={24} className="text-green-600" />
              </div>
            </div>
            <div className="flex-1 ml-4">
              <div className="text-sm font-medium text-gray-600">Total Revenue</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-yellow-100 p-3 rounded-full flex items-center justify-center">
                <AlertCircle size={24} className="text-yellow-600" />
              </div>
            </div>
            <div className="flex-1 ml-4">
              <div className="text-sm font-medium text-gray-600">Outstanding</div>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency(outstandingInvoices)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-cyan-100 p-3 rounded-full flex items-center justify-center">
                <DollarSign size={24} className="text-cyan-600" />
              </div>
            </div>
            <div className="flex-1 ml-4">
              <div className="text-sm font-medium text-gray-600">Pending Quotes</div>
              <div className="text-2xl font-bold text-cyan-600">{formatCurrency(pendingQuotesValue)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg shadow-sm text-white">
          <div className="p-6">
            <h5 className="text-lg font-semibold mb-4">Quick Actions</h5>
            <div className="flex flex-col gap-3">
              <button 
                className="bg-white/20 hover:bg-white/30 text-white border-none rounded-md px-4 py-2 text-sm flex items-center justify-start gap-2 transition-colors" 
                onClick={() => onNavigate('clients')}
              >
                <Users size={16} />
                Manage Clients
              </button>
              <button 
                className="bg-white/20 hover:bg-white/30 text-white border-none rounded-md px-4 py-2 text-sm flex items-center justify-start gap-2 transition-colors" 
                onClick={() => onNavigate('invoices')}
              >
                <FileText size={16} />
                Create Invoice
              </button>
              <button 
                className="bg-white/20 hover:bg-white/30 text-white border-none rounded-md px-4 py-2 text-sm flex items-center justify-start gap-2 transition-colors" 
                onClick={() => onNavigate('quotes')}
              >
                <DollarSign size={16} />
                Create Quote
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h6 className="text-lg font-semibold mb-4">System Status</h6>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Invoices</span>
                <span className="text-sm font-semibold">{invoices.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Quotes</span>
                <span className="text-sm font-semibold">{quotes.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">Overdue Invoices</span>
                <span className="text-sm font-semibold text-red-600">{overdueInvoices}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-yellow-600">Pending Quotes</span>
                <span className="text-sm font-semibold text-yellow-600">{pendingQuotes}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h6 className="text-lg font-semibold mb-4">Quick Links</h6>
            <div className="space-y-2">
              <button 
                className="w-full flex items-center justify-start gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-md transition-colors" 
                onClick={() => onNavigate('reports')}
              >
                <BarChart3 size={16} />
                View Reports
              </button>
              <button 
                className="w-full flex items-center justify-start gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-md transition-colors" 
                onClick={() => onNavigate('settings')}
              >
                <Settings size={16} />
                System Settings
              </button>
              <button 
                className="w-full flex items-center justify-start gap-3 p-3 text-gray-700 hover:bg-gray-50 rounded-md transition-colors" 
                onClick={() => onNavigate('clients')}
              >
                <Users size={16} />
                Manage Clients
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="bg-transparent border-0 pb-0 px-6 pt-6">
            <div className="flex justify-between items-center">
              <h6 className="mb-0 text-lg font-semibold">Recent Activity</h6>
              <div className="flex gap-2">
                <button className="px-3 py-1 text-sm border border-blue-600 text-blue-600 rounded hover:bg-blue-50 transition-colors" onClick={() => onNavigate('invoices')}>
                  View All Invoices
                </button>
                <button className="px-3 py-1 text-sm border border-blue-600 text-blue-600 rounded hover:bg-blue-50 transition-colors" onClick={() => onNavigate('quotes')}>
                  View All Quotes
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Number</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Client</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Amount</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Status</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map(item => {
                      const client = clients.find(c => c._id === item.clientId);
                      const subtotal = item.items?.reduce((sum, lineItem) => 
                        sum + (lineItem.quantity * lineItem.price), 0) || 0;
                      const discount = subtotal * (item.discountPercentage || 0) / 100;
                      const afterDiscount = subtotal - discount;
                      const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
                      const total = afterDiscount + tax;

                      return (
                        <tr key={`${item._id}-${item.number}`} className="hover:bg-gray-50 border-b border-gray-100">
                          <td className="py-3 px-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.items ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'
                            }`}>
                              {item.items ? 'Invoice' : 'Quote'}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-bold">{item.number}</td>
                          <td className="py-3 px-3">{client?.name || 'Unknown'}</td>
                          <td className="py-3 px-3">{new Date(item.date).toLocaleDateString()}</td>
                          <td className="py-3 px-3">{formatCurrency(total)}</td>
                          <td className="py-3 px-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.status === 'paid' ? 'bg-green-100 text-green-800' : 
                              item.status === 'overdue' ? 'bg-red-100 text-red-800' : 
                              item.status === 'sent' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <button className="inline-flex items-center px-3 py-1 border border-blue-600 text-blue-600 rounded text-sm hover:bg-blue-50 transition-colors">
                              <Eye size={14} className="mr-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardHome;